import { Injectable } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { AuditContextService } from './audit-context.service';

/** Prisma's default of 5s is too tight once a transaction spans a whole handler. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Runs work in a transaction whose first statement publishes the current actor
 * to Postgres, so `audit.fn_log_change()` can attribute every row the
 * transaction touches.
 *
 * `set_config(..., true)` is transaction-LOCAL, and that is not a detail:
 * Prisma pools connections, so the session-level form would leak one user's
 * identity onto the next request that happened to borrow the same connection.
 * For an audit trail, misattribution is worse than no attribution.
 *
 * Services that already used `prisma.$transaction(async tx => …)` get
 * attribution by switching to this — the callback signature is identical.
 */
@Injectable()
export class AuditTransactionService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly auditContext: AuditContextService
  ) {}

  async run<T>(
    fn: (tx: Prisma.TransactionClient) => Promise<T>,
    options?: { timeout?: number; maxWait?: number }
  ): Promise<T> {
    return this.prisma.$transaction(
      async (tx) => {
        await this.publishActor(tx);
        return fn(tx);
      },
      {
        timeout: options?.timeout ?? DEFAULT_TIMEOUT_MS,
        maxWait: options?.maxWait ?? 5_000,
      }
    );
  }

  /**
   * For a transaction opened elsewhere. Call it as the first statement, before
   * any write — the trigger reads the setting at the moment it fires, so
   * anything written before this point is attributed to `system`.
   */
  async publishActor(tx: Prisma.TransactionClient): Promise<void> {
    const payload = this.auditContext.actorPayload();
    if (!payload) return;

    try {
      await tx.$executeRaw`SELECT set_config('audit.actor', ${payload}, true)`;
    } catch {
      // Attribution is best-effort. The change rows themselves are written by
      // the trigger regardless, and losing the actor must never cost the
      // caller their transaction.
    }
  }
}
